import useFetch from '../../../hooks/useFetch';
import { useState } from 'react';
import { ServiceEvents } from '../shared/interfaces';
import { buildEndpoint } from '../EndPoints';
import { getFetchDefaultHeaders } from '../shared/vars';

const useInapiApplicationService = () => {
    const { doPost, doGet } = useFetch();
    
    const [fetchingCreateApplication, setFetchingCreateApplication] = useState(false);
    const [fetchingUpdatePowerAndSignature, setFetchingUpdatePowerAndSignature] = useState(false);
    const [fetchingConfirmApplication, setFetchingConfirmApplication] = useState(false);
    const [fetchingGetApplication, setFetchingGetApplication] = useState(false);
    const [fetchingUpdateInapiNumber, setFetchingUpdateInapiNumber] = useState(false);
    const [fetchingGeneratePowerPdf, setFetchingGeneratePowerPdf] = useState(false);
    const [fetchingSaveSignedPdf, setFetchingSaveSignedPdf] = useState(false);

    const BASE_PATH = 'brand-registrations/inapi-application';

    const createInapiApplication = (data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/create`),
            body: data,
            setFetching: setFetchingCreateApplication
        });
    };

    const updatePowerAndSignature = (data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/update-power-signature`),
            body: data,
            setFetching: setFetchingUpdatePowerAndSignature
        });
    };

    const confirmInapiApplication = (data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/confirm`),
            body: data,
            setFetching: setFetchingConfirmApplication
        });
    };

    /**
     * Descarga el poder firmado. El endpoint exige sesión, así que se pide con el
     * token y se abre el archivo recibido (window.open no manda el token).
     */
    const downloadPowerDocument = async (verificationId: number) => {
        // Se abre la pestaña en el clic, antes de esperar la descarga: si se abre
        // después del await, Safari/Firefox la bloquean como popup.
        const tab = window.open('', '_blank');
        try {
            const url = buildEndpoint(`${BASE_PATH}/${verificationId}/download-power`);
            const response = await fetch(url, { headers: getFetchDefaultHeaders() });
            if (!response.ok) {
                throw new Error('No se pudo descargar el poder simple.');
            }
            const contentType = response.headers.get('Content-Type') || '';
            let target: string | null = null;
            if (contentType.includes('application/json')) {
                // Algunas versiones responden con la URL del archivo
                const body = await response.json();
                target = body?.data?.url || body?.data?.download_url || body?.data?.power_document_url || null;
            } else {
                target = URL.createObjectURL(await response.blob());
                setTimeout(() => URL.revokeObjectURL(target as string), 60000);
            }
            if (!target) throw new Error('No se encontró el documento.');
            if (tab) tab.location.href = target;
            else window.location.href = target;
        } catch (error) {
            if (tab) tab.close();
            throw error;
        }
    };

    const getInapiApplication = (verificationId: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${verificationId}/show`),
            setFetching: setFetchingGetApplication
        });
    };

    const updateInapiNumber = (data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/update-inapi-number`),
            body: data,
            setFetching: setFetchingUpdateInapiNumber
        });
    };

    /**
     * Genera el PDF del poder simple, lo guarda y retorna la URL
     * Usado para generar el preview antes de firmar
     */
    const generatePowerPdf = (data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/generate-power-pdf-save`),
            body: data,
            setFetching: setFetchingGeneratePowerPdf
        });
    };

    /**
     * Guarda el PDF firmado por el usuario
     */
    const saveSignedPdf = (data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/save-signed-pdf`),
            body: data,
            setFetching: setFetchingSaveSignedPdf
        });
    };

    const [fetchingRegeneratePowerDocument, setFetchingRegeneratePowerDocument] = useState(false);

    const regeneratePowerDocument = (data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/regenerate-power-document`),
            body: data,
            setFetching: setFetchingRegeneratePowerDocument
        });
    };

    // --- Firma del poder por enlace público ---

    const [fetchingSignatureLink, setFetchingSignatureLink] = useState(false);
    const [fetchingSendSignatureEmail, setFetchingSendSignatureEmail] = useState(false);
    const [fetchingPowerSignature, setFetchingPowerSignature] = useState(false);
    const [fetchingSignPower, setFetchingSignPower] = useState(false);

    /** Genera (o recupera) el enlace público de firma. Solo abogado/admin. */
    const generateSignatureLink = (data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/generate-signature-link`),
            body: data,
            setFetching: setFetchingSignatureLink
        });
    };

    /** Envía al solicitante el correo con el enlace de firma. Solo abogado/admin. */
    const sendSignatureEmail = (data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/send-signature-email`),
            body: data,
            setFetching: setFetchingSendSignatureEmail
        });
    };

    /** Datos del poder para la pantalla pública de firma. No requiere sesión. */
    const getPowerSignatureByToken = (token: string, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/power-signature/${token}`),
            setFetching: setFetchingPowerSignature
        });
    };

    /** Envía la firma desde la pantalla pública. No requiere sesión. Va como FormData por el PDF. */
    const signPowerByToken = (token: string, data: FormData, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/power-signature/${token}/sign`),
            body: data,
            setFetching: setFetchingSignPower
        });
    };

    return {
        fetchingSignatureLink,
        fetchingSendSignatureEmail,
        fetchingPowerSignature,
        fetchingSignPower,
        generateSignatureLink,
        sendSignatureEmail,
        getPowerSignatureByToken,
        signPowerByToken,
        fetchingCreateApplication,
        fetchingUpdatePowerAndSignature,
        fetchingConfirmApplication,
        fetchingGetApplication,
        fetchingUpdateInapiNumber,
        fetchingGeneratePowerPdf,
        fetchingSaveSignedPdf,
        fetchingRegeneratePowerDocument,
        createInapiApplication,
        updatePowerAndSignature,
        confirmInapiApplication,
        downloadPowerDocument,
        getInapiApplication,
        updateInapiNumber,
        generatePowerPdf,
        saveSignedPdf,
        regeneratePowerDocument
    };
};

export default useInapiApplicationService;

